import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';

import { err, ok } from './common/Result';

import type { Result } from './common/Result';
import type { CommentRepository } from '@src/repositories/CommentRepository';
import type {
  ClipCommentCount,
  ClipRef,
  Comment,
  CreateCommentInput,
  Drawing,
  Reactions,
} from '@src/types.ts/comment';

const logger = LogManager.getLogger('service:CommentService');

type RepositoryDeps = Pick<
  CommentRepository,
  | 'listByClip'
  | 'countByClips'
  | 'create'
  | 'updateBody'
  | 'updateTimestamp'
  | 'updateDrawing'
  | 'setResolved'
  | 'addReaction'
  | 'removeReaction'
  | 'delete'
>;

/**
 * Stateless orchestration over `CommentRepository`. Validates inputs and
 * returns `Result` discriminated unions — no React/MobX, no IO of its own.
 */
export class CommentService {
  // Resolver indirection: the repository is constructed lazily by
  // DatoClientStore after auth is wired, so hold a getter, not a direct ref.
  constructor(private readonly getRepository: () => RepositoryDeps) {}

  listByClip(videoId: string): Promise<Result<Comment[]>> {
    if (!videoId) {
      return Promise.resolve(err('VALIDATION', 'videoId is required'));
    }
    return this.run('list comments', repo => repo.listByClip(videoId));
  }

  /**
   * Total comment count per Video record (top-level + replies) for a batch of
   * videos — used by the clips list indicator. An empty or all-invalid `refs`
   * resolves to an empty list, not an error.
   */
  countByClips(refs: ClipRef[]): Promise<Result<ClipCommentCount[]>> {
    if (refs.length === 0) return Promise.resolve(ok([]));
    return this.run('count comments by clip', repo => repo.countByClips(refs));
  }

  /** Create a top-level comment, or a reply when `input.parentId` is set. */
  createComment(input: CreateCommentInput): Promise<Result<{ id: string }>> {
    if (!input.videoId) {
      return Promise.resolve(err('VALIDATION', 'videoId is required'));
    }
    if (!input.body.trim()) {
      return Promise.resolve(err('VALIDATION', 'body is required'));
    }
    if (!input.authorName.trim()) {
      return Promise.resolve(err('VALIDATION', 'authorName is required'));
    }
    return this.run('create comment', repo => repo.create(input));
  }

  /**
   * Replace or remove a comment's marks (PLAT-11292). The payload is sanitized
   * in the repository, so an unreadable stroke is dropped rather than stored;
   * passing `null` clears the drawing outright.
   */
  updateDrawing(id: string, drawing: Drawing | null): Promise<Result<void>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    return this.run('update comment drawing', repo =>
      repo.updateDrawing(id, drawing),
    );
  }

  editComment(id: string, body: string): Promise<Result<void>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    if (!body.trim()) {
      return Promise.resolve(err('VALIDATION', 'body is required'));
    }
    return this.run('edit comment', repo => repo.updateBody(id, body));
  }

  /**
   * Reposition a comment on the timeline, or unpin it with `null`. Separate
   * from `editComment` because moving is not a text edit: it leaves
   * `comment_edited_at` untouched.
   */
  moveComment(
    id: string,
    videoTimeSeconds: number | null,
  ): Promise<Result<void>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    if (
      videoTimeSeconds !== null &&
      (!Number.isFinite(videoTimeSeconds) || videoTimeSeconds < 0)
    ) {
      return Promise.resolve(
        err('VALIDATION', 'videoTimeSeconds must be a non-negative number'),
      );
    }
    return this.run('move comment', repo =>
      repo.updateTimestamp(id, videoTimeSeconds),
    );
  }

  setResolved(id: string, resolved: boolean): Promise<Result<void>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    return this.run('resolve comment', repo => repo.setResolved(id, resolved));
  }

  addReaction(
    id: string,
    emoji: string,
    authorIdentifier: string,
  ): Promise<Result<{ id: string; reactions: Reactions }>> {
    if (!id || !emoji || !authorIdentifier) {
      return Promise.resolve(
        err('VALIDATION', 'id, emoji and authorIdentifier are required'),
      );
    }
    return this.run('add reaction', repo =>
      repo.addReaction(id, emoji, authorIdentifier),
    );
  }

  removeReaction(
    id: string,
    emoji: string,
    authorIdentifier: string,
  ): Promise<Result<{ id: string; reactions: Reactions }>> {
    if (!id || !emoji || !authorIdentifier) {
      return Promise.resolve(
        err('VALIDATION', 'id, emoji and authorIdentifier are required'),
      );
    }
    return this.run('remove reaction', repo =>
      repo.removeReaction(id, emoji, authorIdentifier),
    );
  }

  /** Delete a comment; a top-level comment cascades to its replies. */
  deleteComment(id: string): Promise<Result<void>> {
    if (!id) return Promise.resolve(err('VALIDATION', 'id is required'));
    return this.run('delete comment', repo => repo.delete(id));
  }

  private async run<T>(
    description: string,
    fn: (repo: RepositoryDeps) => Promise<T>,
  ): Promise<Result<T>> {
    let repository: RepositoryDeps;
    try {
      repository = this.getRepository();
    } catch (e) {
      logger.error('Comment repository not initialized', extractErrorInfo(e));
      return err(
        'PRECONDITION',
        e instanceof Error ? e.message : 'Repository not initialized',
        e,
      );
    }
    try {
      return ok(await fn(repository));
    } catch (e) {
      logger.error(`Failed to ${description}`, extractErrorInfo(e));
      return err(
        'EXTERNAL',
        e instanceof Error ? e.message : 'Unknown error',
        e,
      );
    }
  }
}
